/**
 * Damage diagram canvas (Iter 67ec).
 *
 * Renders a vehicle outline SVG + lets the inspector tap/click anywhere
 * on it to plot a damage marker. Selecting an existing marker reveals a
 * popover for changing the kind, adding a note, or removing it. Markers
 * use normalized 0–100 coords so the data survives any viewport size.
 *
 * Touch behaviour: the SVG captures pointerdown directly, so this works
 * unmodified on mobile + tablet + Wacom signing pads (no extra gesture
 * library needed). For now it's tap-to-place; pinch-zoom comes in
 * Phase B along with photo upload + signature pad.
 *
 * Read-only mode is automatic when `canEdit=false` — markers still
 * render, but the SVG ignores clicks and the popover hides edit
 * controls. Used when a phase has been signed off and locked.
 */
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Trash2, Sparkles, Hammer, AlertOctagon, PaintBucket, Minus, X as XIcon,
  Camera, Image as ImageIcon, Loader2, ZoomIn, Maximize2, Minimize2,
} from "lucide-react";
import { diagramFor, ALL_VIEWS, bodyFamilyFor } from "./vehicleDiagrams";
import usePinchZoom from "@/lib/usePinchZoom";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";

// Marker kinds: visible to the inspector via the legend strip. Each
// carries an icon + accent colour. Adding a new kind = one entry here,
// the backend stores it as opaque string.
export const MARKER_KINDS = [
  { id: "scratch", labelKey: "inspection.markerScratch", icon: Sparkles,     color: "#eab308", ring: "ring-yellow-300", bg: "bg-yellow-500" },
  { id: "dent",    labelKey: "inspection.markerDent",    icon: Hammer,       color: "#ea580c", ring: "ring-orange-300", bg: "bg-orange-500" },
  { id: "broken",  labelKey: "inspection.markerBroken",  icon: AlertOctagon, color: "#dc2626", ring: "ring-red-300",    bg: "bg-red-500" },
  { id: "paint",   labelKey: "inspection.markerPaint",   icon: PaintBucket,  color: "#9333ea", ring: "ring-purple-300", bg: "bg-purple-500" },
  { id: "missing", labelKey: "inspection.markerMissing", icon: Minus,        color: "#6b7280", ring: "ring-gray-300",   bg: "bg-gray-500" },
];

const KIND_BY_ID = Object.fromEntries(MARKER_KINDS.map((m) => [m.id, m]));

function uid() {
  // Browsers everywhere we care about expose crypto.randomUUID; the
  // fallback covers ancient WebKit in case a vendor's iPad is locked
  // to an old iOS.
  try { return crypto.randomUUID(); } catch {
    return `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}

/**
 * Convert a pointer event (clientX/clientY) to a 0–100 coord pair
 * inside the SVG's viewBox. Works for both mouse + touch.
 */
function pointerToSvg(evt, svgEl) {
  const r = svgEl.getBoundingClientRect();
  const x = ((evt.clientX - r.left) / r.width) * 100;
  const y = ((evt.clientY - r.top) / r.height) * 60;
  return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(60, y)) };
}

export default function DamageDiagramCanvas({
  bodyType,
  markers,
  onChange,         // (next: Marker[]) => void
  canEdit = true,
  testId = "damage-diagram",
  // Iter 67ed — extra markers rendered behind the active set in a
  // dimmer style so vendors can spot newly-added damage at a glance.
  // Used by the return phase to overlay the pickup phase's markers.
  compareMarkers = null,
  compareLabel = null,
}) {
  const { t } = useTranslation();
  const family = useMemo(() => bodyFamilyFor(bodyType), [bodyType]);
  const [activeView, setActiveView] = useState(ALL_VIEWS[0]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeKind, setActiveKind] = useState(MARKER_KINDS[0].id);
  const svgRef = useRef(null);
  const zoom = usePinchZoom();
  // Iter 106 — fullscreen popup mode: the inline canvas is cramped on
  // phones, so an expand button re-renders this same component instance
  // inside a full-viewport Dialog (state, markers and handlers carry over).
  const [expanded, setExpanded] = useState(false);

  const Diagram = useMemo(() => diagramFor(family, activeView), [family, activeView]);
  const viewMarkers = (markers || []).filter((m) => m.view === activeView);
  const viewCompare = (compareMarkers || []).filter((m) => m.view === activeView);
  const selected = (markers || []).find((m) => m.id === selectedId) || null;

  // Tap on empty SVG → create a new marker at the tap coord using the
  // currently active kind. Tap on a marker bubbles up via the marker's
  // own handler and stops here.
  const handleSvgPointerDown = (evt) => {
    if (!canEdit || !svgRef.current) return;
    if (evt.target.dataset?.marker === "1") return; // tap landed on a marker
    const { x, y } = pointerToSvg(evt, svgRef.current);
    const m = {
      id: uid(),
      view: activeView,
      x, y,
      kind: activeKind,
      note: "",
    };
    onChange?.([...(markers || []), m]);
    setSelectedId(m.id);
  };

  const updateMarker = (id, patch) => {
    onChange?.((markers || []).map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };
  const deleteMarker = (id) => {
    onChange?.((markers || []).filter((m) => m.id !== id));
    setSelectedId(null);
  };

  // Iter 67ee — photo upload for the selected marker. Reuses the shared
  // `/api/upload` endpoint (PNG/JPG/WEBP up to 8 MB) and stores the
  // returned storage path on the marker. We don't upload to S3 directly
  // from the browser to keep the existing access-control + serve flow
  // (admins / vendors / customers on the booking can see attachments
  // via `/api/files/:path`).
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);
  // Iter 67ef — preview modal state. We use the same component for any
  // marker's photo; storing the URL is enough since the dialog renders
  // a single image and a close X.
  const [previewUrl, setPreviewUrl] = useState(null);
  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file || !selected) return;
    setPhotoUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      updateMarker(selected.id, { photo_path: data?.path || null });
      toast.success(t("inspection.photoUploaded", "Photo attached"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPhotoUploading(false);
    }
  };

  const content = (
    <div className={`rounded-2xl border border-amber-200 bg-white p-3 sm:p-4 ${expanded ? "border-0 p-0 sm:p-0" : ""}`} data-testid={testId}>
      {/* ── Toolbar: view tabs + marker-kind legend ───────────────── */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex rounded-full border border-border bg-neutral-50 p-0.5" role="tablist" data-testid={`${testId}-views`}>
          {ALL_VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => { setActiveView(v); setSelectedId(null); }}
              className={`text-[11px] font-semibold px-3 py-1.5 rounded-full transition ${
                activeView === v ? "bg-neutral-900 text-amber-400" : "text-neutral-600 hover:text-neutral-900"
              }`}
              data-testid={`${testId}-view-${v}`}
            >
              {t(`inspection.view${v.charAt(0).toUpperCase() + v.slice(1)}`, v)}
            </button>
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold ml-2">
          {t("inspection.markerCount", { count: viewMarkers.length })}
        </div>
      </div>

      {/* ── Marker-kind picker. Tapping a kind makes it the active
            stamp for the next placement. Doubles as the colour legend. */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`${testId}-kinds`}>
        {MARKER_KINDS.map((k) => {
          const isOn = activeKind === k.id;
          const Icon = k.icon;
          return (
            <button
              key={k.id}
              type="button"
              disabled={!canEdit}
              onClick={() => setActiveKind(k.id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition border ${
                isOn
                  ? `${k.bg} text-white border-transparent shadow-sm`
                  : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-400"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`${testId}-kind-${k.id}`}
            >
              <Icon className="h-3 w-3" />
              {t(k.labelKey, k.id)}
            </button>
          );
        })}
      </div>

      {/* ── Diagram canvas ────────────────────────────────────────── */}
      <div
        className="relative rounded-xl bg-neutral-50 ring-1 ring-neutral-200 overflow-hidden touch-none"
        style={{ touchAction: "none" }}
        {...zoom.handlers}
        data-testid={`${testId}-zoom-container`}
      >
        <div
          // Inner transform layer. CSS transform-origin centre means
          // pinch zooms toward the canvas centre, not the top-left.
          style={{
            transform: `translate(${zoom.pan.x}px, ${zoom.pan.y}px) scale(${zoom.scale})`,
            transformOrigin: "center center",
            transition: zoom.isZoomed ? "none" : "transform 160ms ease-out",
          }}
        >
        <svg
          ref={svgRef}
          viewBox="0 0 100 60"
          preserveAspectRatio="xMidYMid meet"
          className={`w-full aspect-[5/3] text-neutral-800 select-none block ${canEdit ? "cursor-crosshair" : "cursor-default"}`}
          onPointerDown={handleSvgPointerDown}
          style={{ touchAction: "manipulation" }}
          data-testid={`${testId}-svg`}
        >
          <Diagram />
          {/* Iter 67ed — compare-overlay markers rendered FIRST so the
              active phase's markers paint on top. Compare markers use a
              dashed ring + lower opacity to read as "ghosts". */}
          {viewCompare.map((m) => {
            const k = KIND_BY_ID[m.kind] || KIND_BY_ID.scratch;
            return (
              <g key={`cmp-${m.id}`} transform={`translate(${m.x},${m.y})`} data-testid={`${testId}-compare-marker-${m.id}`}>
                <circle r="2.2" fill="none" stroke={k.color} strokeWidth="0.45" strokeDasharray="0.8,0.6" opacity="0.85" />
                <circle r="1.1" fill={k.color} opacity="0.35" />
              </g>
            );
          })}
          {/* Marker overlay */}
          {viewMarkers.map((m) => {
            const k = KIND_BY_ID[m.kind] || KIND_BY_ID.scratch;
            const isSel = m.id === selectedId;
            return (
              <g
                key={m.id}
                transform={`translate(${m.x},${m.y})`}
                data-marker="1"
                onPointerDown={(e) => { e.stopPropagation(); setSelectedId(m.id); }}
                style={{ cursor: canEdit ? "pointer" : "default" }}
                data-testid={`${testId}-marker-${m.id}`}
              >
                {/* Iter 67ee — accessible <title> doubles as native browser tooltip
                    so vendors auditing long-term rentals can hover any marker to
                    see who placed it + when, without opening the popover. */}
                <title>
                  {(KIND_BY_ID[m.kind]?.id || m.kind).toUpperCase()}
                  {m.note ? ` — ${m.note}` : ""}
                  {m.created_at ? `\nPlotted ${new Date(m.created_at).toLocaleString()}` : ""}
                </title>
                {/* Halo for selected */}
                {isSel && <circle r="3.2" fill="none" stroke={k.color} strokeWidth="0.6" opacity="0.5" />}
                <circle r="1.8" fill={k.color} stroke="white" strokeWidth="0.4" data-marker="1" />
                {m.photo_path && (
                  /* Tiny camera glyph in the marker corner so vendors can see at a
                     glance which markers have photo evidence without opening each
                     popover. The path mirrors the lucide camera icon. */
                  <g transform="translate(1.3,-1.3) scale(0.07)" pointerEvents="none">
                    <circle r="11" fill="white" stroke={k.color} strokeWidth="2" />
                    <path d="M -6 -2 L -3 -2 L -2 -4 L 2 -4 L 3 -2 L 6 -2 L 6 5 L -6 5 Z"
                          fill="none" stroke={k.color} strokeWidth="1.6" />
                    <circle r="2.2" fill={k.color} />
                  </g>
                )}
              </g>
            );
          })}
        </svg>
        </div>

        {/* Iter 67ed — zoom toolbar. Sits over the canvas top-left so
            it doesn't collide with the popover (top-right). Hidden on
            small screens where pinch is the primary gesture. */}
        <div className="absolute top-2 left-2 flex gap-1.5" data-testid={`${testId}-zoom-toolbar`}>
          <div className="inline-flex rounded-full bg-white/90 shadow ring-1 ring-neutral-200 backdrop-blur">
            <button
              type="button"
              onClick={() => zoom.reset()}
              disabled={!zoom.isZoomed}
              className="h-7 px-2.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-neutral-700 disabled:opacity-40"
              aria-label={t("inspection.zoomReset", "Reset zoom")}
              data-testid={`${testId}-zoom-reset`}
            >
              {Math.round(zoom.scale * 100)}%
            </button>
          </div>
          <button
            type="button"
            onClick={() => { setExpanded((v) => !v); setSelectedId(null); zoom.reset(); }}
            className="h-7 w-7 rounded-full bg-white/90 shadow ring-1 ring-neutral-200 backdrop-blur inline-flex items-center justify-center text-neutral-700 hover:text-neutral-900"
            aria-label={expanded ? t("inspection.collapseDiagram", "Close fullscreen") : t("inspection.expandDiagram", "Open fullscreen")}
            data-testid={`${testId}-expand-btn`}
          >
            {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        </div>

        {/* Compare-overlay legend ribbon */}
        {viewCompare.length > 0 && (
          <div
            className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-white/90 shadow ring-1 ring-neutral-200 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold text-neutral-600"
            data-testid={`${testId}-compare-legend`}
          >
            <span className="inline-block h-2 w-2 rounded-full ring-1 ring-neutral-400 ring-dashed" />
            {compareLabel || t("inspection.compareLegend", "Previous")} · {viewCompare.length}
          </div>
        )}

        {/* Selected-marker popover. Floats top-right of the canvas so it
            doesn't fight with phone keyboards on the note field. */}
        {selected && (
          <div
            className="absolute top-2 right-2 w-[260px] max-w-[88%] rounded-xl bg-white shadow-lg ring-1 ring-neutral-200 p-3"
            data-testid={`${testId}-popover`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
                {t("inspection.markerEdit")}
              </span>
              <button
                type="button"
                aria-label={t("common.close")}
                onClick={() => setSelectedId(null)}
                className="ml-auto h-6 w-6 rounded-full bg-neutral-100 hover:bg-neutral-200 inline-flex items-center justify-center transition"
                data-testid={`${testId}-popover-close`}
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-5 gap-1 mb-2">
              {MARKER_KINDS.map((k) => {
                const isOn = selected.kind === k.id;
                const Icon = k.icon;
                return (
                  <button
                    key={k.id}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => updateMarker(selected.id, { kind: k.id })}
                    className={`flex flex-col items-center justify-center gap-1 py-1.5 rounded-lg border text-[9px] font-bold uppercase tracking-wider transition ${
                      isOn ? `${k.bg} text-white border-transparent` : "bg-white border-neutral-200 text-neutral-700"
                    } disabled:opacity-50`}
                    title={t(k.labelKey, k.id)}
                    aria-label={t(k.labelKey, k.id)}
                    data-testid={`${testId}-popover-kind-${k.id}`}
                  >
                    <Icon className="h-3 w-3" />
                  </button>
                );
              })}
            </div>
            <Input
              value={selected.note || ""}
              onChange={(e) => updateMarker(selected.id, { note: e.target.value.slice(0, 500) })}
              disabled={!canEdit}
              placeholder={t("inspection.notePlaceholder")}
              className="h-8 text-xs rounded-lg"
              data-testid={`${testId}-popover-note`}
            />

            {/* Iter 67ee — photo evidence per marker. Inline thumbnail
                when present, hidden file input + camera button otherwise. */}
            <div className="mt-2" data-testid={`${testId}-popover-photo`}>
              {selected.photo_path ? (
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewUrl(fileUrl(selected.photo_path))}
                    className="relative block rounded-lg overflow-hidden ring-1 ring-neutral-200 hover:ring-amber-400 transition shrink-0 group"
                    data-testid={`${testId}-popover-photo-thumb`}
                    aria-label={t("inspection.previewPhoto", "Open photo")}
                  >
                    <img
                      src={fileUrl(selected.photo_path)}
                      alt={t("inspection.markerPhoto", "Marker photo")}
                      className="h-12 w-16 object-cover"
                    />
                    {/* Iter 67ef — magnifying-glass affordance on hover so
                        users know the thumbnail is clickable. */}
                    <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center text-white opacity-0 group-hover:opacity-100">
                      <ZoomIn className="h-4 w-4" />
                    </span>
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => updateMarker(selected.id, { photo_path: null })}
                      className="text-[10px] text-neutral-500 hover:text-rose-700 underline"
                      data-testid={`${testId}-popover-photo-remove`}
                    >
                      {t("inspection.removePhoto", "Remove photo")}
                    </button>
                  )}
                </div>
              ) : canEdit && (
                <>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/heic"
                    onChange={handlePhotoPick}
                    capture="environment"
                    className="hidden"
                    data-testid={`${testId}-popover-photo-input`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={photoUploading}
                    className="w-full rounded-full h-7 text-[11px]"
                    data-testid={`${testId}-popover-photo-button`}
                  >
                    {photoUploading
                      ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      : <Camera className="h-3 w-3 mr-1" />}
                    {t("inspection.addPhoto", "Add photo")}
                  </Button>
                </>
              )}
            </div>

            {selected.created_at && (
              <p className="mt-2 text-[10px] text-neutral-500" data-testid={`${testId}-popover-timestamp`}>
                {t("inspection.plottedAt", "Plotted")}{" "}
                <time dateTime={selected.created_at}>
                  {new Date(selected.created_at).toLocaleString()}
                </time>
              </p>
            )}
            {canEdit && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => deleteMarker(selected.id)}
                className="mt-2 rounded-full h-7 text-[11px] text-rose-700 hover:text-rose-900 hover:bg-rose-50 w-full justify-center"
                data-testid={`${testId}-popover-delete`}
              >
                <Trash2 className="h-3 w-3 mr-1" /> {t("inspection.deleteMarker")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Hint strip below the canvas */}
      {canEdit && (
        <p className="mt-2 text-[11px] text-neutral-500">
          {t("inspection.canvasHint")}
        </p>
      )}

      {/* Iter 67ef — photo preview lightbox. Shadcn Dialog already manages
          focus trap + esc-to-close + backdrop click; we just supply the
          image. Image scales to fit the viewport so vendors can inspect
          damage on a phone or a 4K external monitor without scrolling. */}
      <Dialog open={!!previewUrl} onOpenChange={(open) => !open && setPreviewUrl(null)}>
        <DialogContent
          className="max-w-5xl p-2 bg-neutral-950 border-0"
          data-testid={`${testId}-photo-preview-dialog`}
        >
          {previewUrl && (
            <img
              src={previewUrl}
              alt={t("inspection.markerPhoto", "Marker photo")}
              className="max-h-[85vh] w-full object-contain rounded-lg"
              data-testid={`${testId}-photo-preview-img`}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  if (!expanded) return content;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) { setExpanded(false); zoom.reset(); } }}>
      <DialogContent
        className="w-[100vw] max-w-[100vw] h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[92vh] sm:max-w-4xl rounded-none sm:rounded-2xl p-3 pt-10 sm:p-6 overflow-y-auto"
        data-testid={`${testId}-fullscreen-dialog`}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
}
